/**
 * @file TournamentContext.tsx
 * @description Tournament context with payment integration
 * 
 * Task 19.7: Converted from JavaScript to TypeScript
 * Reference: .taskmaster/docs/task-19-tournament-context-payment.md
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';

import { getTournamentDetails, getCryptoTradingPairList, CryptoTradingPairListItem } from 'api/tournamentService';
import { useUser } from 'api/userService';
import { getPaymentStatus } from 'api/paymentService';
import { EntryFee, TournamentResponse } from '../types/tournament';
import { PaymentStatus } from '../types/payment';
import { DEFAULT_FREE_ENTRY_FEE, TournamentContextValue, TournamentProviderProps } from './TournamentContext/types';
import { isPaidTournament as isPaidTournamentHelper, isPaymentRequired as isPaymentRequiredHelper } from './TournamentContext/helpers';
// Task 39.2: Import PaymentContext for integration
import { PaymentContext } from './PaymentContext/PaymentContext';

// Context with undefined default (will throw if used outside provider)
const TournamentContext = createContext<TournamentContextValue | undefined>(undefined);

/**
 * Hook to consume TournamentContext
 * @returns TournamentContextValue or undefined if outside provider
 */
export const useTournament = (): TournamentContextValue => {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error(
      'useTournament must be used within a TournamentProvider. ' +
        'Wrap your component tree with <TournamentProvider>.'
    );
  }
  return context;
};

export const useOptionalTournament = (): TournamentContextValue | undefined => {
  return useContext(TournamentContext);
};

const TOURNAMENT_STATUSES: Record<string, string> = {
  new: 'BEFORE_START',
  before_start: 'BEFORE_START',
  pending_payment: 'pending_payment',
  in_progress: 'IN_PROGRESS',
  finishing: 'FINISHING',
  finished: 'FINISHED',
  cancelled: 'CANCELLED',
};

export const TournamentProvider: React.FC<TournamentProviderProps> = ({
  children,
  tournamentId,
  initialTournamentDetails = null,
  loadTradingPairs = true,
}) => {
  const [tournamentStatus, setTournamentStatus] = useState<string | null>(null);
  const [joinedStatus, setJoinedStatus] = useState<boolean | null>(null);
  const [tournamentDetails, setTournamentDetails] = useState<TournamentResponse | null>(
    initialTournamentDetails ?? null,
  );
  const hasFetchedTournamentDetailsRef = React.useRef(false);
  const tournamentDetailsInFlightRef = React.useRef(false);
  const lastTournamentIdRef = React.useRef<string | number>(tournamentId);
  const tournamentStatusSocketOverrideRef = React.useRef<{ status: string | null; ts: number | null }>({
    status: null,
    ts: null,
  });
  const tournamentDetailsRequestIdRef = React.useRef(0);
  const { isLoggedIn, getUserDetailsWithTournaments, userId } = useUser();
  // Task 39.3: Typed tournamentPairList with CryptoTradingPairListItem
  const [tournamentPairList, setTournamentPairList] = useState<CryptoTradingPairListItem[]>([]);

  const [myAssets, setMyAssets] = useState<unknown>(null);
  
  // Task 19.2: Entry fee state
  const [entryFee, setEntryFee] = useState<EntryFee | null>(null);
  
  // Task 19.3: Payment status state
  const [userPaymentStatus, setUserPaymentStatus] = useState<PaymentStatus | null>(null);
  const [isCheckingPaymentStatus, setIsCheckingPaymentStatus] = useState<boolean>(false);
  const [paymentStatusError, setPaymentStatusError] = useState<Error | null>(null);
  
  // Task 39.2: Access PaymentContext (may be null if not in tree)
  const paymentContextValue = useContext(PaymentContext);

  const normalizeBackendTournamentStatus = useCallback((backendStatus: unknown): string | null => {
    if (typeof backendStatus !== 'string' || backendStatus.length === 0) {
      return null;
    }

    const statusKey = backendStatus.toLowerCase();
    return TOURNAMENT_STATUSES[statusKey] ?? backendStatus;
  }, []);

  const getTournamentStatusRank = useCallback((status: string | null): number => {
    if (!status) {
      return -1;
    }
    if (status === 'BEFORE_START' || status === 'new') {
      return 0;
    }
    if (status === 'pending_payment') {
      return 1;
    }
    if (status === 'IN_PROGRESS' || status === 'in_progress') {
      return 2;
    }
    if (status === 'FINISHING' || status === 'finishing') {
      return 3;
    }
    if (status === 'FINISHED' || status === 'finished') {
      return 4;
    }
    if (status === 'CANCELLED' || status === 'cancelled') {
      return 5;
    }
    return 10;
  }, []);

  const applySocketStatusOverride = useCallback((candidateStatus: string | null): string | null => {
    const socketStatus = tournamentStatusSocketOverrideRef.current.status;
    if (!socketStatus) {
      return candidateStatus;
    }

    return getTournamentStatusRank(socketStatus) >= getTournamentStatusRank(candidateStatus)
      ? socketStatus
      : candidateStatus;
  }, [getTournamentStatusRank]);

  // TEAM_007: SSOT status update from Socket.IO event tournamentStatusChanged
  const applyTournamentStatusChangedEvent = useCallback((event: unknown): void => {
    try {
      if (!event || typeof event !== 'object') {
        return;
      }

      const payload = event as any;
      const eventTournamentId = payload?.tournamentId ?? payload?.data?.tournamentId;
      if (eventTournamentId == null || String(eventTournamentId) !== String(tournamentId)) {
        return;
      }

      const tsRaw = payload?.timestamp ?? payload?.data?.timestamp;
      const tsMs = typeof tsRaw === 'string' ? Date.parse(tsRaw) : null;
      const lastTs = tournamentStatusSocketOverrideRef.current.ts;

      if (typeof tsMs === 'number' && Number.isFinite(tsMs) && lastTs != null && tsMs <= lastTs) {
        return;
      }

      const newStatusRaw = payload?.newStatus ?? payload?.data?.newStatus;
      const normalizedNewStatus = normalizeBackendTournamentStatus(newStatusRaw);
      if (!normalizedNewStatus) {
        return;
      }

      tournamentStatusSocketOverrideRef.current = {
        status: normalizedNewStatus,
        ts: typeof tsMs === 'number' && Number.isFinite(tsMs) ? tsMs : Date.now(),
      };

      setTournamentStatus((prev) => {
        return getTournamentStatusRank(normalizedNewStatus) >= getTournamentStatusRank(prev)
          ? normalizedNewStatus
          : prev;
      });
    } catch (error) {
      console.error('[TournamentContext][TEAM_007] Failed to apply tournamentStatusChanged event', error);
    }
  }, [tournamentId, normalizeBackendTournamentStatus, getTournamentStatusRank]);
  
  // Task 19.3: Check user payment status method
  // Task 39.1: Added tournamentId validation to prevent cross-tournament payment status leaks
  const checkUserPaymentStatus = useCallback(async (
    targetTournamentId: string | number,
    paymentId: string
  ): Promise<void> => {
    if (!paymentId) {
      setUserPaymentStatus(null);
      return;
    }
    
    setIsCheckingPaymentStatus(true);
    setPaymentStatusError(null);
    
    try {
      const response = await getPaymentStatus(paymentId);
      
      // Task 39.1: Validate that payment belongs to the expected tournament
      // This prevents cross-tournament payment status leaks (security invariant)
      if (String(response.tournamentId) !== String(targetTournamentId)) {
        console.warn(
          `[TournamentContext] Payment ${paymentId} belongs to tournament ${response.tournamentId}, ` +
          `but was checked for tournament ${targetTournamentId}. Ignoring payment status.`
        );
        setUserPaymentStatus(null);
        return;
      }
      
      setUserPaymentStatus(response.status);
    } catch (error) {
      setPaymentStatusError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsCheckingPaymentStatus(false);
    }
  }, []);

  const isJoinedToTournamentWithId = async (checkTournamentId: string | number): Promise<boolean> => {
    if (isLoggedIn) {
      try {
        const userDetails = await getUserDetailsWithTournaments();
        return userDetails?.tournaments?.some(
          (tournament: { id: string | number }) => tournament.id === checkTournamentId
        ) ?? false;
      } catch (error) {
        console.error(error);
        return false;
      }
    } else {
      return false;
    }
  };

  const applyTournamentDetails = useCallback((
    details: TournamentResponse | null,
    userDetails?: { tournaments?: Array<{ id: string | number }> } | null,
  ): void => {
    if (!details) {
      setTournamentDetails(null);
      return;
    }

    const normalizedDetails: TournamentResponse = (details as any)?.data?.data && !(details as any)?.data?.attributes
      ? ({
          ...(details as any),
          data: (details as any).data.data,
        } as TournamentResponse)
      : details;

    console.log('[TournamentContext][debug] raw details', details);
    console.log('[TournamentContext][debug] normalized details data', normalizedDetails?.data);

    setTournamentDetails(normalizedDetails);

    const rawData: any = normalizedDetails?.data;
    const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
    const backendStatus: string | undefined = attributes?.status;
    console.log('[TournamentContext][debug] derived attributes/status', {
      tournamentId,
      backendStatus,
      attributes,
    });

    // Derive joined flag from participants
    let isUserJoined = false;
    const currentUserId = userId;

    if (currentUserId != null && attributes?.participants) {
      const participants: any = attributes.participants;
      const participantsArray: any[] = Array.isArray(participants?.data)
        ? participants.data
        : Array.isArray(participants)
        ? participants
        : [];

      isUserJoined = participantsArray.some((participant: any) => {
        const participantId =
          participant?.id ??
          participant?.user?.id ??
          participant;
        return String(participantId) === String(currentUserId);
      });
    }

    // Fallback: also treat user as joined if backend user.tournaments includes this id
    if (!isUserJoined && userDetails && Array.isArray(userDetails.tournaments)) {
      const fromTournaments = userDetails.tournaments.some(
        (t: { id: string | number }) => String(t.id) === String(tournamentId),
      );
      if (fromTournaments) {
        isUserJoined = true;
      }
    }

    setJoinedStatus((prev) => {
      if (prev === true) {
        return true;
      }
      return !!isUserJoined;
    });

    let customStatus = normalizeBackendTournamentStatus(backendStatus);

    if (backendStatus === 'pending_payment' && isUserJoined) {
      customStatus = 'IN_PROGRESS';
    }

    setTournamentStatus((prev) => {
      if (!customStatus) {
        return prev;
      }

      const nextStatus = applySocketStatusOverride(customStatus);
      if (!prev) {
        return nextStatus;
      }
      return getTournamentStatusRank(nextStatus) >= getTournamentStatusRank(prev)
        ? nextStatus
        : prev;
    });

    const extractedEntryFee = attributes?.entryFee ?? DEFAULT_FREE_ENTRY_FEE;
    setEntryFee(extractedEntryFee);
  }, [tournamentId, userId, applySocketStatusOverride, normalizeBackendTournamentStatus]);

  // TEAM_003: Explicit backend-driven refresh of tournament state after payment/join.
  // Reuses getTournamentDetails and getUserDetailsWithTournaments to avoid any local hacks
  // and keeps backend as the single source of truth for joinedStatus and tournamentStatus.
  const refreshTournamentState = useCallback(async (
    detailsOverride?: TournamentResponse | null,
  ): Promise<void> => {
    try {
      console.log('[TournamentContext][TEAM_003] Refreshing tournament state for', tournamentId);

      const details = detailsOverride ?? await getTournamentDetails(tournamentId);
      const userDetails = detailsOverride
        ? null
        : isLoggedIn
        ? await getUserDetailsWithTournaments()
        : null;

      applyTournamentDetails(details, userDetails as { tournaments?: Array<{ id: string | number }> } | null);
    } catch (error) {
      console.error('[TournamentContext][TEAM_003] Failed to refresh tournament state:', error);
    }
  }, [tournamentId, isLoggedIn, getUserDetailsWithTournaments, applyTournamentDetails]);

  useEffect(() => {
    if (!loadTradingPairs) {
      return;
    }

    const checkTournamentPairList = async () => {
      const detailsId = tournamentDetails?.data?.id;
      if (detailsId) {
        try {
          const apiData = await getCryptoTradingPairList(detailsId);
          setTournamentPairList(apiData);
        } catch (error) {
          console.error("Error fetching tournament pair id:", error);
        }
      }
    };
    checkTournamentPairList();
  }, [tournamentDetails, loadTradingPairs]);

  useEffect(() => {
    if (lastTournamentIdRef.current !== tournamentId) {
      lastTournamentIdRef.current = tournamentId;
      tournamentDetailsRequestIdRef.current += 1;
      hasFetchedTournamentDetailsRef.current = false;
      tournamentDetailsInFlightRef.current = false;
    }

    // Optimistic first paint from initialTournamentDetails (passed via
    // react-router location.state when navigating to /game/:id). This data
    // persists across F5 — HTML5 history state survives page reload — so it
    // can be stale by hours. Apply it for zero-flicker, but ALWAYS fetch fresh
    // from the backend afterwards. Monotonic status ranking
    // (getTournamentStatusRank) prevents the fetch from downgrading the
    // status if a Socket event already advanced it.
    if (initialTournamentDetails && !hasFetchedTournamentDetailsRef.current) {
      applyTournamentDetails(initialTournamentDetails);
    }

    if (hasFetchedTournamentDetailsRef.current || tournamentDetailsInFlightRef.current) {
      return;
    }

    const fetchTournamentDetails = async () => {
      try {
        const requestId = ++tournamentDetailsRequestIdRef.current;
        tournamentDetailsInFlightRef.current = true;
        console.log('[TournamentContext] Fetching tournament details for', tournamentId);
        const details = await getTournamentDetails(tournamentId);
        console.log('[TournamentContext][debug] fetched details before apply', details);
        if (requestId !== tournamentDetailsRequestIdRef.current) {
          return;
        }
        applyTournamentDetails(details);
        hasFetchedTournamentDetailsRef.current = true;
      } catch (error) {
        console.error('Failed to fetch tournament details:', error);
        hasFetchedTournamentDetailsRef.current = true;
      } finally {
        tournamentDetailsInFlightRef.current = false;
      }
    };

    fetchTournamentDetails();

    return;
  }, [tournamentId, userId, initialTournamentDetails, applyTournamentDetails]);

  useEffect(() => {
    console.log('[TournamentContext] State snapshot', {
      tournamentId,
      tournamentStatus,
      joinedStatus,
      hasEntryFee: !!entryFee,
      userPaymentStatus,
    });
  }, [tournamentId, tournamentStatus, joinedStatus, entryFee, userPaymentStatus]);

  useEffect(() => {
    if (!tournamentDetails || tournamentStatus) {
      return;
    }

    const rawData: any = tournamentDetails?.data;
    const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
    const backendStatus: string | undefined = attributes?.status;
    const normalizedStatus = normalizeBackendTournamentStatus(backendStatus);

    if (normalizedStatus) {
      setTournamentStatus((prev) => prev ?? applySocketStatusOverride(normalizedStatus));
    }
  }, [tournamentDetails, tournamentStatus, normalizeBackendTournamentStatus, applySocketStatusOverride]);

  // If user is joined but backend status is still pending_payment, treat it as IN_PROGRESS
  useEffect(() => {
    if (tournamentStatus === 'pending_payment' && joinedStatus) {
      setTournamentStatus('IN_PROGRESS');
    }
  }, [tournamentStatus, joinedStatus]);

  useEffect(() => {
    (async () => {
      try {
        const isJoined = await isJoinedToTournamentWithId(tournamentId);
        setJoinedStatus((prev) => {
          // Preserve joined=true from any other source (e.g. tournament participants)
          if (prev === true) {
            return true;
          }
          return !!isJoined;
        });
      } catch (error) {
        console.error(
          '[TournamentContext] Failed to resolve joined status from user tournaments:',
          error,
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  // Task 39.2: Sync userPaymentStatus from PaymentContext when session matches this tournament
  useEffect(() => {
    if (!paymentContextValue?.session) {
      return;
    }
    
    const sessionTournamentId = paymentContextValue.session.tournamentId;
    if (String(sessionTournamentId) === String(tournamentId)) {
      // Session belongs to this tournament - sync status
      if (paymentContextValue.currentStatus) {
        setUserPaymentStatus(paymentContextValue.currentStatus);
      }
    }
  }, [paymentContextValue?.session, paymentContextValue?.currentStatus, tournamentId]);

  // Task 39.2: Dynamic hasActivePaymentSession based on PaymentContext
  // Returns true only when PaymentContext has a session for THIS tournament
  const hasActivePaymentSession = useMemo(() => {
    if (!paymentContextValue?.session) {
      return false;
    }
    return String(paymentContextValue.session.tournamentId) === String(tournamentId);
  }, [paymentContextValue?.session, tournamentId]);

  // Task 19.6: Computed values
  const isPaidTournament = isPaidTournamentHelper(entryFee);
  const isPaymentRequired = isPaymentRequiredHelper(entryFee, userPaymentStatus, joinedStatus);

  const value: TournamentContextValue = {
    tournamentStatus,
    joinedStatus,
    tournamentDetails,
    isJoinedToTournamentWithId,
    refreshTournamentState,
    applyTournamentDetails,
    applyTournamentStatusChangedEvent,
    setMyAssets,
    myAssets,
    tournamentPairList,
    tournamentId,
    // Task 19.2: Entry fee
    entryFee,
    // Task 19.3: Payment status
    userPaymentStatus,
    isCheckingPaymentStatus,
    paymentStatusError,
    checkUserPaymentStatus,
    // Task 19.5: PaymentContext integration
    hasActivePaymentSession,
    // Task 19.6: Computed values
    isPaidTournament,
    isPaymentRequired,
  };

  return <TournamentContext.Provider value={value}>{children}</TournamentContext.Provider>;
};

export default TournamentContext;
